import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic,
  MicOff,
  Volume2,
  Loader2,
  Sparkles,
  RefreshCw,
  MessageSquare,
  Zap,
  Download,
  FileText,
  Check,
  AlertCircle,
  Clock,
  Award,
  BarChart3,
  Video,
  Play,
  UploadCloud,
  ChevronDown,
  ArrowRight,
  RotateCcw,
  CheckCircle2,
  Target,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
} from "lucide-react";
import PageHead from "@/components/PageHead";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAR_CRITERIA = [
  { key: "situation", label: "Situation", desc: "Context & background" },
  { key: "task", label: "Task", desc: "Goal & challenge" },
  { key: "action", label: "Action", desc: "Key steps you took" },
  { key: "result", label: "Result", desc: "Impact & outcome" },
];

export default function InterviewCopilot() {
  const { user, refreshBalance } = useAuth();
  const fileInputRef = useRef(null);

  const [duration, setDuration] = useState("30 minutes");
  const [interviewType, setInterviewType] = useState("behavioral");
  const [difficulty, setDifficulty] = useState("medium");
  const [jd, setJd] = useState("");
  const [resumeText, setResumeText] = useState("");
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [phase, setPhase] = useState("setup");

  const [qaBank, setQaBank] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [qaRawText, setQaRawText] = useState("");
  const [isDownloadingQABank, setIsDownloadingQABank] = useState(false);
  const [isStartingPractice, setIsStartingPractice] = useState(false);
  const [isExportingQA, setIsExportingQA] = useState(false);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [sessionResults, setSessionResults] = useState([]);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const [report, setReport] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isExportingReport, setIsExportingReport] = useState(false);

  const loadProfile = () => {
    const p = user?.profile;
    if (!p) {
      toast.info("No saved profile found. Please upload a CV.");
      return;
    }
    const lines = [
      user?.name ? `Name: ${user.name}` : "",
      user?.email ? `Email: ${user.email}` : "",
      p.target_role ? `Target Role: ${p.target_role}` : "",
      p.current_role ? `Current Role: ${p.current_role}` : "",
      p.skills?.length ? `Skills: ${p.skills.join(", ")}` : "",
      p.bio ? `Summary: ${p.bio}` : "",
    ].filter(Boolean);

    setResumeText(lines.join("\n\n"));
    setFileName("Career_Profile_Resume");
    toast.success("Loaded resume from your profile!");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/ai/parse-resume", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = res.data?.text || res.data?.extracted_text || "";
      setResumeText(text);
      toast.success("Resume uploaded & parsed!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not parse resume file. Please paste text directly.");
    } finally {
      setIsUploading(false);
    }
  };

  const speakQuestion = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.0;
      window.speechSynthesis.speak(utterance);
    } else {
      toast.info("Speech synthesis not supported in this browser.");
    }
  };

  const toggleRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Speech recognition is not supported in your browser. Please type your answer.");
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    } else {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setUserAnswer((prev) => (prev ? prev + " " + transcript : transcript));
      };

      recognition.onerror = () => {
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
      toast.info("Microphone listening... Speak your answer.");
    }
  };

  useEffect(() => {
    if (phase === "practice") {
      setTimerSeconds(0);
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds((s) => s + 1);
      }, 1000);
    } else {
      clearInterval(timerIntervalRef.current);
    }
    return () => clearInterval(timerIntervalRef.current);
  }, [phase, currentIdx]);

  const getDurationCredits = (d) => {
    if (d?.includes("15")) return 5;
    if (d?.includes("45")) return 12;
    return 8;
  };

  const generateQABank = async () => {
    if (!duration) {
      toast.error("Interview Duration is mandatory. Please select a duration.");
      return;
    }
    if (!interviewType) {
      toast.error("Interview Type is mandatory. Please select an interview type.");
      return;
    }
    if (!difficulty) {
      toast.error("Difficulty Level is mandatory. Please select a difficulty.");
      return;
    }
    if (!resumeText.trim()) {
      toast.error("Resume is mandatory. Please upload your Resume (PDF/DOCX) or click 'Use Career Profile'.");
      return;
    }
    if (!jd.trim()) {
      toast.error("Job Description is mandatory. Please paste the target Job Description.");
      return;
    }
    setIsDownloadingQABank(true);

    try {
      const { data } = await api.post("/ai/interview-generate-qa-bank", {
        resume_text: resumeText,
        job_description: jd,
        duration,
        interview_type: interviewType,
        difficulty,
        mode: "qabank_only",
      });

      setQaBank(data?.qa_bank);
      const qList = data?.questions || [];
      setQuestions(qList);
      setQaRawText(data?.qa_text || "");
      setPhase("qabank_ready");
      refreshBalance?.();
      toast.success("15-question tailored Q&A bank generated! (4 Credits used)");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to generate Q&A bank.");
    } finally {
      setIsDownloadingQABank(false);
    }
  };

  const exportQABank = async (fmt = "pdf") => {
    if (!qaRawText) return;
    setIsExportingQA(true);

    try {
      const response = await api.post(
        "/ai/interview-export-qa-bank",
        { qa_text: qaRawText, format: fmt },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: fmt === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Interview_QA_Bank.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded Q&A Bank (${fmt.toUpperCase()})`);
    } catch (err) {
      console.error("Export QA Bank error:", err);
      toast.error(`Could not download .${fmt.toUpperCase()} Q&A bank.`);
    } finally {
      setIsExportingQA(false);
    }
  };

  const startPractice = async () => {
    if (!duration) {
      toast.error("Interview Duration is mandatory. Please select a duration.");
      return;
    }
    if (!interviewType) {
      toast.error("Interview Type is mandatory. Please select an interview type.");
      return;
    }
    if (!difficulty) {
      toast.error("Difficulty Level is mandatory. Please select a difficulty.");
      return;
    }
    if (!resumeText.trim()) {
      toast.error("Resume is mandatory. Please upload your Resume (PDF/DOCX) or click 'Use Career Profile'.");
      return;
    }
    if (!jd.trim()) {
      toast.error("Job Description is mandatory. Please paste the target Job Description.");
      return;
    }

    const creditsRequired = getDurationCredits(duration);

    if (questions.length === 0) {
      setIsStartingPractice(true);
      try {
        const { data } = await api.post("/ai/interview-generate-qa-bank", {
          resume_text: resumeText,
          job_description: jd,
          duration,
          interview_type: interviewType,
          difficulty,
          mode: "live_practice",
        });
        setQaBank(data?.qa_bank);
        setQuestions(data?.questions || []);
        setQaRawText(data?.qa_text || "");
        refreshBalance?.();
        toast.success(`Live practice session started! (${creditsRequired} Credits used)`);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to initialize questions.");
        setIsStartingPractice(false);
        return;
      } finally {
        setIsStartingPractice(false);
      }
    }
    setCurrentIdx(0);
    setUserAnswer("");
    setCurrentFeedback(null);
    setSessionResults([]);
    setPhase("practice");
  };

  const evaluateSingleAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error("Please type or speak your answer before submitting.");
      return;
    }
    const currentQ = questions[currentIdx] || { question: "Interview Question" };
    setIsEvaluating(true);

    try {
      const { data } = await api.post("/ai/interview-feedback", {
        question: currentQ.question || currentQ.q,
        answer: userAnswer,
        role: user?.profile?.target_role || "Candidate",
      });

      setCurrentFeedback(data);
      const resultItem = {
        question: currentQ.question || currentQ.q,
        user_answer: userAnswer,
        ideal_answer: currentQ.ideal_answer || data?.model_answer,
        score: data?.overall || 80,
        star_scores: data?.star_scores || { situation: 80, task: 80, action: 80, result: 80 },
        strengths: data?.strengths || [],
        improvements: data?.improvements || [],
        section: currentQ.section || "General",
      };

      setSessionResults((prev) => [...prev, resultItem]);
      refreshBalance?.();
      toast.success("Answer evaluated with STAR breakdown!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not evaluate answer.");
    } finally {
      setIsEvaluating(false);
    }
  };

  const nextQuestion = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx((i) => i + 1);
      setUserAnswer("");
      setCurrentFeedback(null);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } else {
      finishSession();
    }
  };

  const finishSession = async () => {
    setIsGeneratingReport(true);
    setPhase("report");

    let compiledResults = [...sessionResults];
    if (userAnswer.trim() && currentQ && !compiledResults.some((r) => r.question === (currentQ.question || currentQ.q))) {
      compiledResults.push({
        question: currentQ.question || currentQ.q,
        user_answer: userAnswer,
        ideal_answer: currentQ.ideal_answer || "",
        score: currentFeedback?.overall || 82,
        star_scores: currentFeedback?.star_scores || { situation: 80, task: 80, action: 80, result: 80 },
        strengths: currentFeedback?.strengths || [],
        improvements: currentFeedback?.improvements || [],
        section: currentQ.section || "General",
      });
    }

    try {
      const { data } = await api.post("/ai/interview-evaluate-session", {
        session_results: compiledResults,
        job_description: jd,
        resume_text: resumeText,
        duration,
      });
      setReport(data);
      toast.success("Interview report generated!");
    } catch (err) {
      console.error("Failed to generate report:", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not generate session report.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const exportPerformanceReport = async (fmt = "pdf") => {
    if (!report) return;
    setIsExportingReport(true);

    try {
      const response = await api.post(
        "/ai/interview-export-feedback-report",
        { report, format: fmt },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: fmt === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Interview_Performance_Report.${fmt}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded Report (${fmt.toUpperCase()})`);
    } catch (err) {
      console.error("Export report error:", err);
      toast.error(`Could not download .${fmt.toUpperCase()} report.`);
    } finally {
      setIsExportingReport(false);
    }
  };

  const formatMMSS = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const currentQ = questions[currentIdx] || {
    question: "Tell me about a challenging project you led and how you managed delivery constraints.",
  };

  return (
    <div className="max-w-6xl mx-auto pb-16 px-2 sm:px-4 space-y-6">
      <PageHead
        label="AI Interview Practice"
        title="Master Your Real-Time Interview with AI"
        sub="Generate role-tailored questions, rehearse out loud with live STAR scoring, and receive comprehensive performance reports."
      />

      {/* Credit & Session Tier Banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-electric">5 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">15-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-violet">8 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">30-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-border/80 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-coral">12 Credits</p>
          <p className="text-xs text-muted-foreground mt-0.5">45-minute session</p>
        </div>
        <div className="card-surface p-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 text-center shadow-xs">
          <p className="text-base sm:text-lg font-bold text-amber-500">4 Credits</p>
          <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">📥 Question Bank Only</p>
        </div>
      </div>

      {/* =========================================================================
          PHASE 1: SESSION CONFIGURATION & QUESTION BANK SETUP
         ========================================================================= */}
      {(phase === "setup" || phase === "qabank_ready") && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl space-y-6">
            <div className="pb-3 border-b border-border/60">
              <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
                ⚙️ Session Configuration
              </h3>
              <p className="text-xs text-muted-foreground">
                All fields marked with <span className="text-rose-500 font-bold">*</span> are mandatory to tailor your mock interview questions accurately.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Duration, Type, Difficulty & JD */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-electric" /> Interview Duration <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-border bg-card px-4 py-3 text-xs sm:text-sm font-medium text-foreground outline-none transition-all hover:border-electric/50 focus:border-electric pr-10 shadow-xs cursor-pointer"
                    >
                      <option value="15 minutes">15 minutes (Fast Practice)</option>
                      <option value="30 minutes">30 minutes (Standard Depth)</option>
                      <option value="45 minutes">45 minutes (Comprehensive)</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 block">
                    🎯 Interview Type <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setInterviewType("behavioral")}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        interviewType === "behavioral"
                          ? "border-violet bg-violet/10 text-foreground font-semibold shadow-xs"
                          : "border-border bg-card hover:bg-secondary text-muted-foreground text-xs"
                      }`}
                    >
                      <p className="text-xs sm:text-sm font-bold">🧠 Behavioral</p>
                      <p className="text-[11px] opacity-80 mt-0.5">STAR-format scenarios</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setInterviewType("technical")}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        interviewType === "technical"
                          ? "border-electric bg-electric/10 text-foreground font-semibold shadow-xs"
                          : "border-border bg-card hover:bg-secondary text-muted-foreground text-xs"
                      }`}
                    >
                      <p className="text-xs sm:text-sm font-bold">💻 Technical</p>
                      <p className="text-[11px] opacity-80 mt-0.5">Deep-dive JD skills</p>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-foreground mb-1.5 block">
                    📊 Difficulty Level <span className="text-rose-500 font-bold">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {["easy", "medium", "hard"].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDifficulty(d)}
                        className={`py-2 px-3 rounded-xl border text-xs font-bold capitalize transition-all ${
                          difficulty === d
                            ? "border-amber-500 bg-amber-500/15 text-amber-600 dark:text-amber-300 shadow-xs"
                            : "border-border bg-card hover:bg-secondary text-muted-foreground"
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-foreground">
                      📋 Job Description <span className="text-rose-500 font-bold">*</span>
                    </label>
                    {!jd.trim() && (
                      <span className="text-[11px] text-rose-500 font-medium">Required</span>
                    )}
                  </div>
                  <textarea
                    rows={4}
                    value={jd}
                    onChange={(e) => setJd(e.target.value)}
                    placeholder="Paste the full job description or requirements here..."
                    className={`w-full resize-none rounded-xl border p-3 text-xs sm:text-sm leading-relaxed outline-none transition-colors ${
                      !jd.trim()
                        ? "border-border bg-background focus:border-electric"
                        : "border-emerald-500/50 bg-background focus:border-emerald-500"
                    }`}
                  />
                </div>
              </div>

              {/* Right Column: Resume Upload & Checklist Card */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                      📄 Upload Your Resume (PDF / DOCX) <span className="text-rose-500 font-bold">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={loadProfile}
                      className="text-xs font-semibold text-electric hover:underline flex items-center gap-1"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Use Career Profile
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    id="interview-cv-upload"
                  />
                  <label
                    htmlFor="interview-cv-upload"
                    className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-xs font-medium transition-all ${
                      resumeText.trim()
                        ? "border-emerald-500/60 bg-emerald-500/10 text-foreground shadow-xs"
                        : "border-dashed border-border bg-secondary/40 hover:border-electric hover:bg-secondary text-muted-foreground"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 pr-2">
                      {isUploading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-electric shrink-0" />
                      ) : resumeText.trim() ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      ) : (
                        <UploadCloud className="h-4 w-4 text-electric shrink-0" />
                      )}
                      <span className="truncate">
                        {isUploading
                          ? "Extracting CV text with AI…"
                          : fileName
                          ? `Resume: ${fileName}`
                          : resumeText.trim()
                          ? "Resume Profile Loaded"
                          : "Upload Resume File (PDF / DOCX) *"}
                      </span>
                    </div>
                    <span className="rounded-md bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground border border-border shadow-xs shrink-0">
                      {resumeText.trim() ? "Change" : "Browse"}
                    </span>
                  </label>
                </div>

                {/* What You'll Get Card */}
                <div className="rounded-2xl border border-border bg-secondary/30 p-4 sm:p-5 space-y-3">
                  <h4 className="font-heading text-xs sm:text-sm font-bold text-foreground flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-lime-600" /> What You'll Get
                  </h4>
                  <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>{interviewType === "behavioral" ? "Behavioral" : "Technical"} + Resume-based questions tailored to JD</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span><strong>15 questions total</strong> per session</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>AI Interviewer — question by question rehearsal</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>Type or speak your answers with per-question timer</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-lime-600 shrink-0" />
                      <span>Full performance report downloadable in PDF & DOCX</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Validation Banner if missing */}
            {(!resumeText.trim() || !jd.trim() || !duration || !interviewType || !difficulty) && (
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-3.5 py-2.5 rounded-xl">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  {!resumeText.trim() && !jd.trim()
                    ? "Resume and Job Description are mandatory to generate questions or start practice."
                    : !resumeText.trim()
                    ? "Please upload your Resume or click 'Use Career Profile' to proceed."
                    : !jd.trim()
                    ? "Please enter the target Job Description to proceed."
                    : "Please ensure all configuration options are selected."}
                </span>
              </div>
            )}
          </div>

          {/* QUESTION BANK ONLY CARD (4 Credits) */}
          <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
                  📥 Question Bank Only — 4 Credits
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Get the full 15-question Q&A bank (with STAR-format suggested answers) as a downloadable PDF & DOCX.
                </p>
              </div>
              <span className="rounded-full bg-amber-500/10 border border-amber-500/20 px-3 py-1 text-xs font-bold text-amber-600 dark:text-amber-300">
                ⚡ Instant Download
              </span>
            </div>

            {phase === "qabank_ready" && qaRawText ? (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2 rounded-xl bg-lime/10 border border-lime/20 p-3.5 text-xs font-semibold text-lime-600 dark:text-lime">
                  <Check className="h-4 w-4" />
                  <span>15 questions generated. 4 credits used. Your downloads are ready below:</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => exportQABank("pdf")}
                    disabled={isExportingQA}
                    className="cv-btn-primary py-3 px-5 text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-glow cursor-pointer"
                  >
                    {isExportingQA ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    <span>Download Q&A Bank (PDF)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => exportQABank("docx")}
                    disabled={isExportingQA}
                    className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-5 text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all shadow-xs cursor-pointer"
                  >
                    {isExportingQA ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-sky-400" />}
                    <span>Download Q&A Bank (DOCX)</span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={generateQABank}
                disabled={isDownloadingQABank || isStartingPractice || !jd.trim() || !resumeText.trim()}
                className="w-full cv-btn-primary py-3.5 px-6 text-sm font-semibold flex items-center justify-between shadow-glow disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  {isDownloadingQABank ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  {isDownloadingQABank ? "Generating Question Bank..." : "Generate & Download Question Bank Only"}
                </span>
                <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold backdrop-blur-xs">
                  4 Credits
                </span>
              </button>
            )}
          </div>

          {/* LIVE MOCK PRACTICE CARD */}
          <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2">
                  🎙️ Live Interactive Mock Practice
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Rehearse question by question, speak aloud, and get immediate STAR scoring with AI audio playback.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={startPractice}
              disabled={isStartingPractice || isDownloadingQABank || !jd.trim() || !resumeText.trim()}
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-3.5 px-6 text-sm font-bold flex items-center justify-between shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isStartingPractice ? (
                <div className="flex items-center justify-center gap-2 w-full">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                  <span>Preparing Live Practice Room...</span>
                </div>
              ) : (
                <>
                  <span className="flex items-center gap-2">
                    <Play className="h-4 w-4 fill-white" />
                    <span>Start Live Practice Session</span>
                  </span>
                  <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold backdrop-blur-xs">
                    {getDurationCredits(duration)} Credits
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* =========================================================================
          PHASE 2: INTERACTIVE QUESTION-BY-QUESTION PRACTICE ROOM
         ========================================================================= */}
      {phase === "practice" && (
        <div className="card-surface p-6 sm:p-8 shadow-card border border-border/90 rounded-2xl space-y-6 animate-in fade-in duration-300">
          {/* Top Progress & Timer Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-border/60">
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-electric text-white text-xs font-bold shadow-xs">
                {currentIdx + 1}
              </span>
              <div>
                <p className="text-xs font-bold text-foreground">
                  Question {currentIdx + 1} of {questions.length || 15}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {currentQ.section || "Core Competency"} · {difficulty.toUpperCase()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-secondary/80 px-3.5 py-1.5 rounded-full border border-border text-xs font-mono font-bold">
                <Clock className="h-3.5 w-3.5 text-electric animate-pulse" />
                <span>{formatMMSS(timerSeconds)}</span>
              </div>
              <button
                onClick={() => setPhase("setup")}
                className="text-xs text-muted-foreground hover:text-foreground underline font-medium"
              >
                Exit Session
              </button>
            </div>
          </div>

          {/* Active Question Prompt Card */}
          <div className="bg-secondary/40 border border-border p-5 rounded-2xl space-y-3">
            <div className="flex items-start justify-between gap-3">
              <p className="font-heading text-sm sm:text-base font-bold text-foreground leading-relaxed">
                "{currentQ.question || currentQ.q}"
              </p>
              <button
                onClick={() => speakQuestion(currentQ.question || currentQ.q)}
                className="rounded-xl border border-border bg-card hover:bg-secondary p-2.5 text-electric shadow-xs shrink-0"
                title="Listen to AI interviewer"
              >
                <Volume2 className="h-4 w-4" />
              </button>
            </div>

            {currentQ.hint && (
              <p className="text-xs text-muted-foreground italic">
                💡 Tip: {currentQ.hint}
              </p>
            )}
          </div>

          {/* Answer Input Area */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-foreground">Your Response</label>
              <button
                type="button"
                onClick={toggleRecording}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  isRecording
                    ? "bg-rose-500 text-white animate-pulse shadow-md"
                    : "bg-secondary text-foreground hover:bg-secondary/80 border border-border"
                }`}
              >
                {isRecording ? <Mic className="h-3.5 w-3.5 animate-pulse text-white" /> : <Mic className="h-3.5 w-3.5 text-rose-500" />}
                {isRecording ? "Stop Recording (Active)" : "Speak Answer"}
              </button>
            </div>

            <textarea
              rows={6}
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Structure your answer using STAR: Describe the Situation, your specific Task, the Action you took, and the final Result..."
              className="w-full resize-none rounded-xl border border-border bg-background p-4 text-xs sm:text-sm leading-relaxed outline-none focus:border-electric font-sans"
            />
          </div>

          {/* Evaluation & Navigation Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <button
              onClick={evaluateSingleAnswer}
              disabled={isEvaluating || !userAnswer.trim()}
              className="cv-btn-primary py-3 px-6 text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-glow"
            >
              {isEvaluating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              <span>Evaluate with STAR</span>
            </button>

            <button
              onClick={nextQuestion}
              className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-5 text-xs sm:text-sm font-semibold flex items-center gap-2 transition-all shadow-xs"
            >
              <span>{currentIdx < questions.length - 1 ? "Next Question" : "Complete & View Report"}</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Real-Time STAR Feedback Box */}
          {currentFeedback && (
            <div className="mt-6 border-t border-border/60 pt-6 space-y-4 animate-in fade-in duration-300">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                  <Award className="h-4 w-4 text-electric" /> STAR Assessment Score: {currentFeedback.overall || 80}/100
                </h4>
              </div>

              {/* STAR Dimension Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {STAR_CRITERIA.map(({ key, label }) => {
                  const score = currentFeedback.star_scores?.[key] || 80;
                  return (
                    <div key={key} className="card-surface p-3 rounded-xl border border-border/80 text-center">
                      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
                      <p className="text-lg font-bold text-electric mt-0.5">{score}%</p>
                    </div>
                  );
                })}
              </div>

              {/* Strengths & Improvements */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="rounded-xl bg-lime/10 border border-lime/20 p-3.5 space-y-1.5">
                  <p className="font-bold text-lime-700 dark:text-lime">💪 Key Strengths</p>
                  {(currentFeedback.strengths || []).map((s, i) => (
                    <p key={i} className="text-muted-foreground">• {s}</p>
                  ))}
                </div>

                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-3.5 space-y-1.5">
                  <p className="font-bold text-amber-700 dark:text-amber-300">⚠️ Areas to Improve</p>
                  {(currentFeedback.improvements || []).map((imp, i) => (
                    <p key={i} className="text-muted-foreground">• {imp}</p>
                  ))}
                </div>
              </div>

              {/* Model STAR Response */}
              {currentFeedback.model_answer && (
                <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1.5 text-xs">
                  <p className="font-bold text-electric flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Exemplary STAR Model Answer
                  </p>
                  <p className="text-muted-foreground leading-relaxed italic">
                    "{currentFeedback.model_answer}"
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* =========================================================================
          PHASE 3: COMPREHENSIVE PERFORMANCE FEEDBACK REPORT
         ========================================================================= */}
      {phase === "report" && (
        <div className="card-surface p-6 sm:p-8 shadow-card border border-border/90 rounded-2xl space-y-8 animate-in fade-in duration-300">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-5 border-b border-border/60">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-electric/15 text-electric">
                  <Award className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold text-foreground">
                    Interview Performance Report
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Candidate: <span className="font-medium text-foreground">{candidateName}</span> · Duration: {duration} · Role: {user?.profile?.target_role || "Target Role"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => exportPerformanceReport("pdf")}
                disabled={isExportingReport}
                className="cv-btn-primary py-2.5 px-4 text-xs font-semibold flex items-center gap-1.5 shadow-glow cursor-pointer"
              >
                {isExportingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                <span>Download Report (PDF)</span>
              </button>
              <button
                type="button"
                onClick={() => exportPerformanceReport("docx")}
                disabled={isExportingReport}
                className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-2.5 px-4 text-xs font-semibold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer"
              >
                {isExportingReport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 text-sky-400" />}
                <span>DOCX</span>
              </button>
            </div>
          </div>

          {/* Scores Overview Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-electric/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Overall Score</p>
              <p className="text-3xl font-extrabold text-electric mt-1.5">{report?.overall_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-violet/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">General / STAR</p>
              <p className="text-3xl font-extrabold text-violet mt-1.5">{report?.general_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-sky-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Technical</p>
              <p className="text-3xl font-extrabold text-sky-500 mt-1.5">{report?.technical_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center bg-card/60 hover:border-emerald-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Resume Match</p>
              <p className="text-3xl font-extrabold text-emerald-500 mt-1.5">{report?.resume_score || 80}<span className="text-xs font-medium text-muted-foreground">/100</span></p>
            </div>
            <div className="card-surface p-4 rounded-xl border border-border text-center col-span-2 sm:col-span-1 bg-card/60 hover:border-lime-500/40 transition-colors">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Hiring Band</p>
              <p className="text-lg font-bold text-lime-600 dark:text-lime-400 mt-2.5">{report?.performance_band || "Strong Hire"}</p>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="rounded-2xl bg-secondary/30 border border-border/80 p-5 sm:p-6 space-y-2.5">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-electric">
              <Sparkles className="h-4 w-4" />
              <span>Overall Assessment</span>
            </div>
            <p className="text-xs sm:text-sm text-foreground leading-relaxed">
              {report?.overall_summary || "Demonstrated structured communication, good domain understanding, and solid alignment with role requirements."}
            </p>
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Key Strengths */}
            <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <h4 className="font-heading text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  Key Strengths
                </h4>
              </div>
              <ul className="space-y-2 text-xs text-foreground/90">
                {(report?.key_strengths || [
                  "Effective STAR situation framing and goal clarity",
                  "Strong technical ownership with impactful delivery examples",
                ]).map((s, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-500 font-bold mt-0.5">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas to Improve */}
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-5 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <h4 className="font-heading text-sm font-bold text-amber-600 dark:text-amber-400">
                  Areas for Enhancement
                </h4>
              </div>
              <ul className="space-y-2 text-xs text-foreground/90">
                {(report?.weak_areas || [
                  "Include quantifiable metrics and financial outcomes in the Result stage",
                  "Keep answer concise to fit within 2-minute response windows",
                ]).map((w, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-amber-500 font-bold mt-0.5">•</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Detailed Competency Feedback Breakdown */}
          {(report?.general_feedback || report?.technical_feedback || report?.resume_feedback) && (
            <div className="space-y-3">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-electric" /> Competency Breakdown
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {report?.general_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      💬 Behavioral / STAR
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.general_feedback}
                    </p>
                  </div>
                )}
                {report?.technical_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      ⚙️ Technical Depth
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.technical_feedback}
                    </p>
                  </div>
                )}
                {report?.resume_feedback && (
                  <div className="rounded-xl border border-border bg-card/60 p-4 space-y-1.5">
                    <p className="font-semibold text-foreground flex items-center gap-1.5">
                      📄 Resume Alignment
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {report.resume_feedback}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actionable Recommendations & Next Steps */}
          {((report?.recommendations && report.recommendations.length > 0) || report?.next_steps) && (
            <div className="rounded-2xl border border-border bg-secondary/20 p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-400" />
                <h4 className="font-heading text-sm font-bold text-foreground">
                  Actionable Recommendations & Next Steps
                </h4>
              </div>
              {report?.recommendations && report.recommendations.length > 0 && (
                <ul className="space-y-2 text-xs text-foreground/90">
                  {report.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-electric font-bold mt-0.5">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              )}
              {report?.next_steps && (
                <div className="pt-3 border-t border-border/50 text-xs">
                  <span className="font-semibold text-foreground">Next Strategic Step: </span>
                  <span className="text-muted-foreground">{report.next_steps}</span>
                </div>
              )}
            </div>
          )}

          {/* Keywords Match Analysis */}
          {((report?.keywords_covered && report.keywords_covered.length > 0) || (report?.keywords_missed && report.keywords_missed.length > 0)) && (
            <div className="space-y-3 pt-2">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <Target className="h-4 w-4 text-electric" /> Target Job Keyword Match
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {report?.keywords_covered && report.keywords_covered.length > 0 && (
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-2">
                    <p className="font-semibold text-emerald-600 dark:text-emerald-400">Covered Keywords ({report.keywords_covered.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {report.keywords_covered.slice(0, 15).map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium text-[11px]">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {report?.keywords_missed && report.keywords_missed.length > 0 && (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                    <p className="font-semibold text-amber-600 dark:text-amber-400">Suggested Keywords to Highlight ({report.keywords_missed.length})</p>
                    <div className="flex flex-wrap gap-1.5">
                      {report.keywords_missed.slice(0, 15).map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium text-[11px]">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Questions Practiced Breakdown */}
          {sessionResults && sessionResults.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-electric" /> Evaluated Questions ({sessionResults.length})
              </h4>
              <div className="space-y-3">
                {sessionResults.map((item, idx) => (
                  <div key={idx} className="rounded-xl border border-border bg-card/60 p-4 space-y-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-foreground">
                        Q{idx + 1}: {item.question}
                      </p>
                      <span className="px-2.5 py-0.5 rounded-full bg-electric/15 text-electric font-bold text-[11px]">
                        {item.score || 80}/100
                      </span>
                    </div>
                    {item.user_answer && (
                      <p className="text-muted-foreground bg-secondary/30 p-2.5 rounded-lg italic">
                        "{item.user_answer}"
                      </p>
                    )}
                    {item.improvements && item.improvements.length > 0 && (
                      <div className="text-[11px] text-amber-500 space-y-0.5">
                        <span className="font-semibold">Coaching Note: </span>
                        {item.improvements.join("; ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Row */}
          <div className="pt-4 flex items-center justify-between border-t border-border/60">
            <button
              type="button"
              onClick={() => {
                setPhase("setup");
                setSessionResults([]);
                setReport(null);
                setCurrentIdx(0);
                setUserAnswer("");
              }}
              className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-5 text-xs sm:text-sm font-semibold flex items-center gap-2 shadow-xs cursor-pointer"
            >
              <RotateCcw className="h-4 w-4" /> Start New Practice Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
